import { CORPUS_STATUSES, type CorpusStatus } from '@rp/contracts'
import { useState } from 'react'
import { isApiError } from '../../api/client'
import { Button, EmptyState, Pager, Spinner, Tabs } from '../../design'
import { canReviewCorpus } from '../../session/roles'
import { useReadySession } from '../../session/useSession'
import { CORPUS_COPY, CORPUS_STATUS_LABELS } from './copy'
import { CorpusEntryList } from './CorpusEntryList'
import { useCorpusEntries, useRetireCorpusEntry } from './queries'

const FIRST_PAGE = 1
const DEFAULT_STATUS: CorpusStatus = 'active'

export const CorpusPage = () => {
  const { membership } = useReadySession()
  const [status, setStatus] = useState<CorpusStatus>(DEFAULT_STATUS)
  const [page, setPage] = useState(FIRST_PAGE)
  const [retiringId, setRetiringId] = useState<string | null>(null)
  const entries = useCorpusEntries(status, page)
  const retire = useRetireCorpusEntry()

  if (!canReviewCorpus(membership.role)) {
    return <EmptyState title={CORPUS_COPY.forbidden} description={CORPUS_COPY.forbiddenBody} />
  }

  if (entries.isPending) return <Spinner label={CORPUS_COPY.loading} />

  if (entries.isError) {
    return (
      <EmptyState
        title={CORPUS_COPY.loadFailed}
        description={isApiError(entries.error) ? entries.error.message : ''}
        action={
          <Button variant="primary" onClick={() => void entries.refetch()}>
            {CORPUS_COPY.retry}
          </Button>
        }
      />
    )
  }

  const { entries: rows, meta } = entries.data
  const pageCount = Math.max(FIRST_PAGE, Math.ceil(meta.total / meta.limit))

  return (
    <section className="corpus">
      <div className="corpus-head">
        <h1>{CORPUS_COPY.title}</h1>
        <p className="corpus-intro">{CORPUS_COPY.intro}</p>
      </div>
      <Tabs
        label={CORPUS_COPY.statuses}
        value={status}
        onChange={(next: CorpusStatus) => {
          setStatus(next)
          setPage(FIRST_PAGE)
        }}
        items={CORPUS_STATUSES.map((value) => ({ value, label: CORPUS_STATUS_LABELS[value] }))}
      />
      <div id={`tabpanel-${status}`} role="tabpanel" aria-labelledby={`tab-${status}`} className="corpus-panel">
        {retire.isError ? (
          <p className="corpus-error tone-danger" role="alert">
            {isApiError(retire.error) ? retire.error.message : CORPUS_COPY.retireFailed}
          </p>
        ) : null}
        <CorpusEntryList
          entries={rows}
          emptyTitle={CORPUS_COPY.empty[status]}
          retiringId={retiringId}
          onRetire={(id) => {
            setRetiringId(id)
            retire.mutate(id, { onSettled: () => setRetiringId(null) })
          }}
        />
        <Pager
          label={CORPUS_COPY.pagination}
          status={CORPUS_COPY.pageStatus(meta.page, meta.total, meta.limit)}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
          previousDisabled={page <= FIRST_PAGE}
          nextDisabled={page >= pageCount}
          previousLabel={CORPUS_COPY.previousPage}
          nextLabel={CORPUS_COPY.nextPage}
        />
      </div>
    </section>
  )
}
